import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { auth } from "../Firebase/firebase-config"
import { onAuthStateChanged } from "firebase/auth"
import AuthService from "./AuthService"
import { countryCodes } from "./CountryCodes"
import "./Login.css"
import { FaArrowCircleLeft, FaInfoCircle } from "react-icons/fa"
import { setPersistence, browserSessionPersistence } from "firebase/auth";
import { ref, set } from "firebase/database";
import { db } from "../Firebase/firebase-config";
import toast from "react-hot-toast";

const LoginScreen = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState("email")
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [isProcessingAuth, setIsProcessingAuth] = useState(false)
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false)
  const [showInfo, setShowInfo] = useState(false);
  const [securityCode, setSecurityCode] = useState("");
  const [securityInput, setSecurityInput] = useState("");

  // Email/Password state
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")

  // Phone/OTP state
  const [phone, setPhone] = useState("")
  const [otp, setOtp] = useState("")
  const [selectedCountry, setSelectedCountry] = useState(countryCodes.find((c) => c.code === "+91") || countryCodes[0])
  const [otpSent, setOtpSent] = useState(false)
  const [confirmationResult, setConfirmationResult] = useState(null)
  const [timeoutSeconds, setTimeoutSeconds] = useState(60)
  const [timer, setTimer] = useState(null)

  // Notification state
  const [notification, setNotification] = useState({ message: "", type: "" })

 const generateSecurityCode = () => {
  const uppercase = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lowercase = "abcdefghijkmnopqrstuvwxyz";
  const numbers = "23456789";

  const getRandomChar = (chars) =>
    chars.charAt(Math.floor(Math.random() * chars.length));

  let code =
    getRandomChar(uppercase) +
    getRandomChar(lowercase) +
    getRandomChar(numbers);

  const allChars = uppercase + lowercase + numbers;

  for (let i = 3; i < 6; i++) {
    code += getRandomChar(allChars);
  }

  // Shuffle
  code = code
    .split("")
    .sort(() => Math.random() - 0.5)
    .join("");

  setSecurityCode(code);
  setSecurityInput("");
};

  useEffect(() => {
    checkAuthentication()
    return () => {
      if (timer) clearInterval(timer)
      AuthService.clearRecaptcha()
    }
  }, [])

  const createSession = async (uid) => {
    const sessionId = Date.now().toString();

    // save in browser
    sessionStorage.setItem("sessionId", sessionId);

    // save in firebase
    await set(ref(db, "sessions/" + uid), {
      sessionId: sessionId,
    });
  };
useEffect(() => {
  generateSecurityCode();
}, []);
  const checkAuthentication = async () => {
    if (isProcessingAuth) return

    try {
      setIsProcessingAuth(true)
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          setShowLoadingOverlay(true)

          const userExists = await AuthService.checkUserExists(user.uid)

          if (userExists) {
            navigate("/dashboard")
          } else {
            navigate("/profile-complete")
          }
          setShowLoadingOverlay(false)
        } else {
          // console.log("👤 No authenticated user found")
        }
        setIsProcessingAuth(false)
      })
    } catch (error) {
      console.error("❌ Error checking authentication:", error)
      setIsProcessingAuth(false)
      setShowLoadingOverlay(false)
    }
  }

  const showNotification = (message, type = "info") => {
    setNotification({ message, type })
    setTimeout(() => setNotification({ message: "", type: "" }), 5000)
  }

  const startTimer = () => {
    setTimeoutSeconds(60)
    const newTimer = setInterval(() => {
      setTimeoutSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(newTimer)
          resetOTPState()
          showNotification("⏰ OTP timeout. Please try again.", "warning")
          return 0
        }
        return prev - 1
      })
    }, 1000)
    setTimer(newTimer)
  }

  const resetOTPState = () => {
    setOtpSent(false)
    setOtp("")
    setConfirmationResult(null)
    setIsLoading(false)
    if (timer) clearInterval(timer)
    AuthService.clearRecaptcha()
  }

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (isProcessingAuth || isLoading) return;

    if (!email || !password) {
      showNotification("Please enter email and password", "error");
      return;
    }

    if (!AuthService.isValidEmail(email)) {
      showNotification("Please enter a valid email address", "error");
      return;
    }
      // Check security code first
  if (!securityInput.trim()) {
    toast.error("Please enter the security code");
    return;
  }

 if (securityInput.trim() !== securityCode) {
  toast.error("Invalid security code");
  generateSecurityCode();
  return;
}
   


    setIsLoading(true);
    setIsProcessingAuth(true);

    try {
      await setPersistence(auth, browserSessionPersistence);
      const userCredential = await AuthService.signInWithEmail(email, password);
      const user = userCredential.user;

      // === NEW: Check account type after login ===
      const userData = await AuthService.getUserProfile(user.uid);

      if (userData) {
        const accountType = userData.type?.toLowerCase();
        const userRole = userData.role?.toLowerCase();

        if (accountType === "partner" || userRole === "viewer") {
          await auth.signOut();
          showNotification(
            "You are registered as a Partner. Please login through 'Login as Partner'.",
            "error"
          );
          return;
        }
      }

      await createSession(user.uid);

      showNotification("✅ Welcome back!", "success");
      setShowLoadingOverlay(true);

      const userExists = await AuthService.checkUserExists(user.uid);

      if (userExists) {
        navigate("/dashboard");
      } else {
        navigate("/profile-complete");
      }
    } catch (error) {
      console.error("❌ Email login failed:", error);
      if (error.message?.includes("User profile not found") ||
        error.message?.includes("not registered")) {
        showNotification("❌ User not registered. Please sign up first.", "error");
        setTimeout(() => navigate("/signup"), 2000);
      } else {
        showNotification(`❌ ${error.message || error}`, "error");
      }
       // Generate a new security code after failed login
    generateSecurityCode();
    } finally {
      setIsLoading(false);
      setIsProcessingAuth(false);
    }
  };
  const handleSendOTP = async () => {
    if (isProcessingAuth || isLoading) return

    if (!phone) {
      showNotification("Please enter phone number", "error")
      return
    }

    if (selectedCountry.code === "+91" && !AuthService.isValidPhone(phone)) {
      showNotification("Please enter a valid 10-digit phone number", "error")
      return
    }

    setIsLoading(true)
    setIsProcessingAuth(true)

    const fullPhoneNumber = `${selectedCountry.code}${phone}`

    try {
      const recaptchaVerifier = AuthService.setupRecaptcha("recaptcha-container")
      showNotification("📱 Sending OTP...", "info")

      const confirmationResult = await AuthService.sendOTP(fullPhoneNumber, recaptchaVerifier)

      setConfirmationResult(confirmationResult)
      setOtpSent(true)
      startTimer()
      showNotification(`✅ OTP sent to ${fullPhoneNumber}`, "success")
    } catch (error) {
      console.error("❌ Error sending OTP:", error)
      showNotification(`❌ ${error}`, "error")
    } finally {
      setIsLoading(false)
      setIsProcessingAuth(false)
    }
  }

  const handleVerifyOTP = async (enteredOtp) => {
    const otpToVerify = enteredOtp || otp  // ✅ use passed OTP first

    if (isProcessingAuth || isLoading) return

    if (!otpToVerify || otpToVerify.length !== 6) {
      showNotification("Please enter 6-digit OTP", "error")
      return
    }

    setIsLoading(true)
    setShowLoadingOverlay(true)

    try {
      await setPersistence(auth, browserSessionPersistence);
      const result = await AuthService.verifyOTP(confirmationResult, otpToVerify)
      const user = result.user
      await createSession(user.uid);

      if (user) {
        const userExists = await AuthService.checkUserExists(user.uid)

        if (userExists) {
          showNotification("✅ Welcome back!", "success")
          navigate("/dashboard")
        } else {
          showNotification("❌ You are not registered. Redirecting...", "error")
          if (timer) clearInterval(timer)
          setTimeout(() => {
            navigate("/profile-complete")
          }, 2000)
        }
      }
    } catch (error) {
      console.error("❌ OTP verification failed:", error)
      showNotification(`❌ ${error}`, "error")
    } finally {
      setIsLoading(false)
      setShowLoadingOverlay(false)
    }
  }


  const handleForgotPassword = async () => {
    if (!email) {
      showNotification("Please enter your email address first", "error")
      return
    }

    if (!AuthService.isValidEmail(email)) {
      showNotification("Please enter a valid email address", "error")
      return
    }

    try {
      await AuthService.resetPassword(email)
      showNotification("✅ Password reset email sent!", "success")
    } catch (error) {
      showNotification(`❌ ${error}`, "error")
    }
  }

  const handleKeyDown = (e, action) => {
    if (e.key === "Enter") {
      e.preventDefault()
      action()
    }
  }
// useEffect(() => {
//   const handleEnterKey = (e) => {
//     if (e.key === "Enter" && email && password && !isLoading) {
//       handleEmailLogin(e);
//     }
//   };

//   window.addEventListener("keydown", handleEnterKey);

//   return () => {
//     window.removeEventListener("keydown", handleEnterKey);
//   };
// }, [email, password, isLoading]);

  const renderEmailTab = () => (
    <div className="auth-form">
      <form onSubmit={handleEmailLogin} className="form-container">
        <div className="input-group">
          <label className="input-label">Email Address</label>
          <div className="input-wrapper">
            <div className="input-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <input
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-input"
            />
          </div>
        </div>

        <div className="input-group">
          <label className="input-label">Password</label>
          <div className="input-wrapper">
            <div className="input-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <circle cx="12" cy="16" r="1" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="form-input"
              style={{ paddingRight: "40px" }}
            />
            <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
        </div>
<div className="security-code-container">
  <div className="security-code-row">

    <div className="security-code-display">
      <span className="security-code-text">
        {securityCode}
      </span>

      <span className="captcha-line line-one"></span>
      <span className="captcha-line line-two"></span>

      <span className="captcha-dot dot-one"></span>
      <span className="captcha-dot dot-two"></span>
      <span className="captcha-dot dot-three"></span>
    </div>

    <button
      type="button"
      className="security-refresh-btn"
      onClick={generateSecurityCode}
      title="Generate new code"
    >
      ↻
    </button>

    <input
      type="text"
      className="security-code-input"
      placeholder="Enter code"
      value={securityInput}
      onChange={(e) => setSecurityInput(e.target.value)}
      maxLength={6}
      autoComplete="off"
    />

  </div>
</div>
        <button type="submit" className="auth-button primary" disabled={isLoading || isProcessingAuth}>
          {isLoading ? (
            <div className="loading-spinner"></div>
          ) : (
            <>
              <span>Sign In</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </>
          )}
        </button>


        <button type="button" className="forgot-password" onClick={handleForgotPassword}>
          Forgot your password?
        </button>
      </form>
      <div className="partner-separator">
        <span>or</span>
      </div>
      <div className="partner-login-container">
        <div className="button-wrapper">
          <button
            className="auth-button partner-login-btn"
            onClick={() => navigate("/partner-login")}
          >
            Login as Partner
          </button>

          <div className="lg-info-icon" onClick={() => setShowInfo(!showInfo)}>
            <FaInfoCircle className="lg-info-icon" />
          </div>
        </div>

        {/* Tooltip */}
        {showInfo && (
          <div className="info-tooltip">
            Partners can log in here using the access assigned by the main user.
          </div>
        )}
      </div>


    </div>
  )

  const renderPhoneTab = () => (
    <div className="auth-form">
      <div className="form-container">
        {!otpSent ? (
          <>
            <div className="input-group">
              <label className="input-label">Phone Number</label>
              <div className="phone-input-wrapper">
                <select
                  value={selectedCountry.code}
                  onChange={(e) => {
                    const country = countryCodes.find((c) => c.code === e.target.value)
                    setSelectedCountry(country)
                  }}
                  className="country-select"
                >
                  {countryCodes.map((country) => (
                    <option key={country.code + country.name} value={country.code}>
                      {country.flag} {country.code}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  placeholder={selectedCountry.code === "+91" ? "Enter 10-digit number" : "Enter phone number"}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => handleKeyDown(e, handleSendOTP)}
                  className="phone-input"
                  maxLength="15"
                />
              </div>
            </div>

            <button className="auth-button primary" onClick={handleSendOTP} disabled={isLoading || isProcessingAuth}>
              {isLoading ? (
                <div className="loading-spinner"></div>
              ) : (
                <>
                  <span>Send OTP</span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                  </svg>
                </>
              )}
            </button>

            <div id="recaptcha-container"></div>
          </>
        ) : (
          <>
            <div className="otp-section">
              <div className="otp-header">
                <div className="otp-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <h3>Verify Your Phone</h3>
                <p>
                  Enter the 6-digit code sent to {selectedCountry.code}
                  {phone}
                </p>
              </div>

              <div className="input-group">
                <div className="otp-input-wrapper">
                  <input
                    type="text"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "").slice(0, 6)
                      setOtp(value)

                      if (value.length === 6) {
                        // Wait slightly for state to update and prevent double triggers
                        setTimeout(() => {
                          handleVerifyOTP(value)
                        }, 300)
                      }
                    }}
                    className="otp-input"
                    maxLength="6"
                  />

                </div>
                <div className="timer-display">
                  <span className={`timer ${timeoutSeconds <= 10 ? "warning" : ""}`}>
                    {Math.floor(timeoutSeconds / 60)}:{(timeoutSeconds % 60).toString().padStart(2, "0")}
                  </span>
                </div>
              </div>

              <button
                className="auth-button primary"
                onClick={handleVerifyOTP}
                disabled={isLoading || isProcessingAuth}
              >
                {isLoading ? (
                  <div className="loading-spinner"></div>
                ) : (
                  <>
                    <span>Verify & Continue</span>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20,6 9,17 4,12" />
                    </svg>
                  </>
                )}
              </button>

              <div className="otp-actions">
                <button className="text-button" onClick={resetOTPState}>
                  Change Number
                </button>
                <button className="text-button" onClick={handleSendOTP} disabled={isLoading || isProcessingAuth}>
                  Resend OTP
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )

  return (
    <div className="login-screen">
      {showLoadingOverlay && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <p>Checking your account...</p>
          </div>
        </div>
      )}
      <div className="login-container">
        <div className="login_back_btn">     <button
          className="login-back-arrow"
          onClick={() => navigate("/")}
        >
          <FaArrowCircleLeft />
        </button></div>

        <div className="login-left">

          <div className="brand-section">

            <div className="brand-logo">
              <div className="logo-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div>
              <span className="brand-name text-white">Altrange</span>
            </div>
            <h1 className="welcome-title">Welcome Back</h1>
            <p className="welcome-subtitle">Sign in to access your account and continue your journey with us.</p>
          </div>

          <div className="features-list">
            <div className="feature-item">
              <div className="feature-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4" />
                  <circle cx="12" cy="12" r="10" />
                </svg>
              </div>
              <span>Secure Authentication</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 1v6m0 6v6" />
                  <path d="M17 8h4l-4-4-4 4h4" />
                  <path d="M7 16H3l4 4 4-4H7" />
                </svg>
              </div>
              <span>Multiple Login Options</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <circle cx="12" cy="16" r="1" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <span>Data Protection</span>
            </div>
          </div>
        </div>

        <div className="login-right">
          <div className="auth-card">

            {notification.message && (
              <div className={`notification ${notification.type}`}>
                <div className="notification-content">
                  <span>{notification.message}</span>
                </div>
              </div>
            )}

            <div className="auth-tabs">

              <button
                className={`tab-button ${activeTab === "email" ? "active" : ""}`}
                onClick={() => setActiveTab("email")}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                Email
              </button>
              <button
                className={`tab-button ${activeTab === "phone" ? "active" : ""}`}
                onClick={() => setActiveTab("phone")}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                Phone
              </button>
            </div>

            {activeTab === "email" ? renderEmailTab() : renderPhoneTab()}

            <div className="auth-footer">
              <p>Don't have an account?</p>
              <button className="signup-link" onClick={() => navigate("/signup")}>
                Create Account
              </button>

            </div>
          </div>
        </div>
      </div>

    </div>

  )
}

export default LoginScreen
